import { ApiResponse, ResponseCode } from '@/common/request/types';
import { reactive, toRefs } from 'vue';
import { useToast } from 'vue-toastification';

interface BaseListViewOptions {
    request: (params: Record<string, any>) => Promise<ApiResponse<any>>;
    paramsBuilder?: () => Record<string, any>;
    hooks?: {
        loadInitialData?: () => Promise<void | any>;
        canFetch?: () => boolean | Promise<boolean>;
        onPageChange?: (offset: number) => void;
        onSuccess?: () => void | Promise<void>;
        transformResponse?: (list: any[]) => any[] | Promise<any[]>;
    };
}

export function useBaseListView(options: BaseListViewOptions) {
    const { request, paramsBuilder, hooks } = options;

    const toast = useToast();

    const state = reactive({
        list: [] as any[],
        total: 0,
        hasLoadedInitialData: false
    });

    const fetchList = async (offset?: number) => {
        if (hooks?.loadInitialData && !state.hasLoadedInitialData) {
            await hooks.loadInitialData();
            state.hasLoadedInitialData = true;
        }

        if (hooks?.canFetch && !(await hooks.canFetch())) return;

        if (offset !== null && offset !== undefined && hooks?.onPageChange) {
            hooks.onPageChange(offset);
        }

        const finalParams = paramsBuilder ? paramsBuilder() : {};

        const response = await request(finalParams);
        if (response.code === ResponseCode.Ok) {
            let list = response.data?.list || response.data || [];
            if (hooks?.transformResponse) {
                list = await hooks.transformResponse(list);
            }

            state.list = list;
            state.total = response.data?.total ?? list.length;

            if (hooks?.onSuccess) {
                await hooks.onSuccess();
            }
        } else {
            state.list = [];
            state.total = 0;
            toast.error(response.data);
        }
    };

    return { ...toRefs(state), fetchList };
}
